// apps/admin/src/stores/useNotificationStore.ts
//
// Phase 8.1 Wave 1 Task 03 — admin notification bell state. Owns:
//   - Notification dropdown list (last 10 items)
//   - Unread count badge
//   - Per-category pending counts (consumed by SidebarItem badges)
//   - WS-driven mutations (add / mark-read / mark-all-read)
//
// vg-binding: CONTEXT:decisions=P8.D-22,P8.D-27,P8.D-150,P8.D-152,P8.D-160,P8.D-161,P8.D-185,P8.D-211,P8.D-212
// vg-binding: UI-SPEC:§5.4
//
// Phase 15 Task 25 — fail-closed UX (P15.D-21): a `forbidden` flag distinguishes
// a 403 AUDIENCE_FORBIDDEN feed (banner + retry) from a real empty feed (empty
// state). `markUnread` supports optimistic mark-read ROLLBACK on server failure.
// vg-binding: CONTEXT:decisions           (P15.D-18, P15.D-21; Task 25)
// vg-binding: INTERFACE-STANDARDS:error-shape
//
// Decisions implemented:
//   - pending-count badges on Sidebar items (Support, Billing
//     Orders). Counts derived from `pendingCountByKey` map.
//   - notification bell, 2 sounds (normal/critical), 7 filter
//     categories (6 backend + virtual "All"), 10-item dropdown
//   - admin reuses @printway/ui Shadcn primitives
//   - Spinner + Skeleton primitives used by NotificationBell
//   - Zustand feature-based slices
//   - WS 4 channels: orders/vendors/support/system
//   - stub backend in P8, real backend lands in Phase 9
//   - 3 collections (notifications, announcements, system_events)
//   - WS event schemas
//
// :
//   - 6 stored categories: orders | products | stores | vendors | billing | system
//   - UI surfaces 7 tabs (6 + virtual "All")
//   - Cross-portal label: admin shows "Billing" for same category vendor calls "Payments"

import { create } from 'zustand';

export type NotificationCategory =
  | 'orders'
  | 'products'
  | 'stores'
  | 'vendors'
  | 'billing'
  | 'system';

/** Virtual tab — NOT a stored category, UI-only "show all" filter. */
export type NotificationFilter = NotificationCategory | 'all';

export const NOTIFICATION_CATEGORIES: readonly NotificationCategory[] = [
  'orders',
  'products',
  'stores',
  'vendors',
  'billing',
  'system',
];

/** Sidebar badge keys — only items with pending counts get a badge. */
export type SidebarBadgeKey = 'support' | 'billing' | 'orders';

export interface AdminNotification {
  id: string;
  category: NotificationCategory;
  /**
   * Title/body as the BE rendered them at dispatch time (recipient's stored
   * `User.language`). Treat as the FALLBACK copy: when `i18nKey` is present the
   * bell re-renders from the catalog at the ACTIVE UI locale, so switching
   * language in the topbar updates existing rows instead of leaving them frozen
   * in the dispatch-time language (CLAUDE.md i18n Tầng 1).
   */
  title: string;
  body: string;
  /** "normal" = subtle ding, "critical" = SLA-breach klaxon. */
  severity: 'normal' | 'critical';
  isRead: boolean;
  createdAt: string;
  /** Optional deep-link href (e.g. `/orders/abc123`). */
  href?: string;
  /** `packages/i18n` NOTIFICATION_KEYS entry — drives locale re-render. */
  i18nKey?: string | null;
  /** Interpolation params for `i18nKey` (subject, preview, merchantName…). */
  i18nParams?: Record<string, unknown> | null;
}

const DROPDOWN_CAP = 10;
const STORE_CAP = 100;

export interface NotificationState {
  /** Most-recent-first ring buffer of cached notifications. */
  items: AdminNotification[];
  /** Map of sidebar item key → pending count badge. */
  pendingCountByKey: Record<SidebarBadgeKey, number>;
  /** Last sound that should play (consumed-and-cleared by NotificationBell). */
  pendingSound: 'normal' | 'critical' | null;
  /** Selected dropdown filter tab. */
  filter: NotificationFilter;
  /** True while initial REST fetch is in flight. */
  loading: boolean;
  /** Last error message (network / API). */
  error: string | null;
  /**
   * True when the feed refetch returned 403 AUDIENCE_FORBIDDEN (P15.D-21 /
   * P15.D-07): the caller has ZERO resolvable audience, so the bell must show a
   * banner + retry, NOT a silent empty state. Distinct from `error` (generic
   * failure) and from an authorized-but-empty feed (`items.length === 0`).
   */
  forbidden: boolean;

  // ---- Selectors --------------------------------------------------------
  unreadCount: () => number;
  visibleItems: () => AdminNotification[];

  // ---- Mutations --------------------------------------------------------
  setFilter: (next: NotificationFilter) => void;
  setLoading: (v: boolean) => void;
  setError: (msg: string | null) => void;
  /** Set the 403 AUDIENCE_FORBIDDEN fail-closed flag (P15.D-21). */
  setForbidden: (v: boolean) => void;
  /** Replace full list (initial REST fetch). */
  setItems: (items: AdminNotification[]) => void;
  /** WS push — prepend new notification. Bumps badge + queues sound. */
  pushNotification: (n: AdminNotification) => void;
  /** Mark single item read (also bumps badge counts downwards). */
  markRead: (id: string) => void;
  /** Revert a single item to unread — optimistic mark-read ROLLBACK (P15.D-21). */
  markUnread: (id: string) => void;
  /** Mark every cached item read. */
  markAllRead: () => void;
  /** Per-item dismiss — removes from local cache (server delete is caller's job). */
  dismiss: (id: string) => void;
  /** Set sidebar pending count (e.g. WS event for a specific surface). */
  setPendingCount: (key: SidebarBadgeKey, count: number) => void;
  /** Bulk-set pending counts (initial fetch). */
  setPendingCounts: (counts: Partial<Record<SidebarBadgeKey, number>>) => void;
  /** Consume the queued sound — returns it and clears the field. */
  consumeSound: () => 'normal' | 'critical' | null;
}

function recomputePending(
  items: AdminNotification[],
  override: Record<SidebarBadgeKey, number>,
): Record<SidebarBadgeKey, number> {
  // If the caller has not provided explicit counts (server-authoritative
  // numbers for the actionable queues), derive a fallback from unread items
  // so badges still surface during WS-only operation.
  const base: Record<SidebarBadgeKey, number> = {
    support: override.support,
    billing: override.billing,
    orders: override.orders,
  };
  // Derive fallbacks ONLY for keys missing in override (i.e. -1 sentinel).
  if (base.orders < 0) {
    base.orders = items.filter(
      (n) => n.category === 'orders' && !n.isRead,
    ).length;
  }
  if (base.billing < 0) {
    base.billing = items.filter(
      (n) => n.category === 'billing' && !n.isRead,
    ).length;
  }
  if (base.support < 0) {
    // Support badge is server-driven (open ticket count). Fall back to 0
    // when no explicit count has been pushed.
    base.support = 0;
  }
  return base;
}

export const useNotificationStore = create<NotificationState>((set, get) => ({
  items: [],
  pendingCountByKey: { support: 0, billing: 0, orders: 0 },
  pendingSound: null,
  filter: 'all',
  loading: false,
  error: null,
  forbidden: false,

  unreadCount: () => get().items.filter((n) => !n.isRead).length,

  visibleItems: () => {
    const { items, filter } = get();
    const visible =
      filter === 'all'
        ? items
        : items.filter((n) => n.category === filter);
    return visible.slice(0, DROPDOWN_CAP);
  },

  setFilter: (next) => set({ filter: next }),
  setLoading: (v) => set({ loading: v }),
  setError: (msg) => set({ error: msg }),
  setForbidden: (v) => set({ forbidden: v }),

  setItems: (items) => {
    const trimmed = items.slice(0, STORE_CAP);
    const overrides = {
      // support is server-authoritative (open-ticket count from
      // useSupportTicketRealtime), NOT derived from notification items.
      // Preserve the already-pushed value so unrelated bell activity does
      // not clobber the sidebar Tickets badge to 0 (debug 2026-07-06).
      support: get().pendingCountByKey.support,
      billing: -1,
      orders: -1,
    } as Record<SidebarBadgeKey, number>;
    set({
      items: trimmed,
      pendingCountByKey: recomputePending(trimmed, overrides),
    });
  },

  pushNotification: (n) => {
    const exists = get().items.some((x) => x.id === n.id);
    if (exists) return;
    const next = [n, ...get().items].slice(0, STORE_CAP);
    const overrides = {
      support: get().pendingCountByKey.support,
      billing: -1,
      orders: -1,
    } as Record<SidebarBadgeKey, number>;
    set({
      items: next,
      pendingCountByKey: recomputePending(next, overrides),
      pendingSound: n.severity,
    });
  },

  markRead: (id) => {
    const next = get().items.map((n) =>
      n.id === id ? { ...n, isRead: true } : n,
    );
    const overrides = {
      support: get().pendingCountByKey.support,
      billing: -1,
      orders: -1,
    } as Record<SidebarBadgeKey, number>;
    set({
      items: next,
      pendingCountByKey: recomputePending(next, overrides),
    });
  },

  markUnread: (id) => {
    const next = get().items.map((n) =>
      n.id === id ? { ...n, isRead: false } : n,
    );
    const overrides = {
      support: get().pendingCountByKey.support,
      billing: -1,
      orders: -1,
    } as Record<SidebarBadgeKey, number>;
    set({
      items: next,
      pendingCountByKey: recomputePending(next, overrides),
    });
  },

  markAllRead: () => {
    const next = get().items.map((n) => ({ ...n, isRead: true }));
    set({
      items: next,
      // Marking notification-bell items read must NOT zero the support
      // badge — ticket unread state is owned by the server-side stats
      // query, not the bell dropdown (debug 2026-07-06).
      pendingCountByKey: {
        support: get().pendingCountByKey.support,
        billing: 0,
        orders: 0,
      },
    });
  },

  dismiss: (id) => {
    const next = get().items.filter((n) => n.id !== id);
    const overrides = {
      support: get().pendingCountByKey.support,
      billing: -1,
      orders: -1,
    } as Record<SidebarBadgeKey, number>;
    set({
      items: next,
      pendingCountByKey: recomputePending(next, overrides),
    });
  },

  setPendingCount: (key, count) =>
    set({
      pendingCountByKey: {
        ...get().pendingCountByKey,
        [key]: Math.max(0, count),
      },
    }),

  setPendingCounts: (counts) => {
    const current = get().pendingCountByKey;
    set({
      pendingCountByKey: {
        support: Math.max(0, counts.support ?? current.support),
        billing: Math.max(0, counts.billing ?? current.billing),
        orders: Math.max(0, counts.orders ?? current.orders),
      },
    });
  },

  consumeSound: () => {
    const s = get().pendingSound;
    if (s) set({ pendingSound: null });
    return s;
  },
}));
